import { useCustomizeControlsStore } from "@/modules/agent/states/cusomize-controls";
import { filterListOfControlsBasedOnTabsAndAgentType, getValidControlNumber } from "./helper";
import { AgentSessionType } from "@/modules/sessions/types";
import { useContext, useMemo } from "react";
import { NistCheckboxContext } from "../../CustomizeControlsTable";
import { X } from "lucide-react";
import { ZButton } from "@/shared/components/button";
import { getCustomizeControlsActions } from "@/modules/agent/states/cusomize-controls";
import Fuse from "fuse.js";
import { searchAndFilteringList } from "../../CustomizeControlsTable/helper";

const CustomizeControlTopLeftSection = () => {
  const selectedControls = useCustomizeControlsStore().selectedControls;
  const selectedTab = useCustomizeControlsStore().selectedTab;
  const agentType = useCustomizeControlsStore().agentType;
  const controls = useCustomizeControlsStore().dataState;
  const changesMade = useCustomizeControlsStore().changesMade;
  const showCheckboxes = useCustomizeControlsStore().showCheckboxes;
  const isCustomControls = useCustomizeControlsStore().isCustomControls;
  const activeBulkAction = useCustomizeControlsStore().activeBulkAction;
  const searchQuery = useCustomizeControlsStore().searchQuery;

  const { showCheckboxes: showNistCheckboxes, setShowCheckboxes: setShowNistCheckboxes } = useContext(NistCheckboxContext);

  // Use useMemo with explicit dependency on controls length and changesMade
  const validControls = useMemo(() => {
    const filtered = getValidControlNumber({
      agentType,
      controls,
      selectedTab,
    });

    return filtered;
  }, [agentType, controls, selectedTab, controls.length, changesMade]);

  // Calculate filtered controls count when there's a search query
  const filteredControlsCount = useMemo(() => {
    if (!searchQuery || !agentType) {
      return validControls.length;
    }

    // Get the data state based on selected tab and agent type (same logic as in CustomizeControlsTable)
    const dataStateBasedOnSelected =
      agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT
        ? controls // Show all controls for vendor assessment
        : controls.filter((c) =>
          filterListOfControlsBasedOnTabsAndAgentType({
            agentType,
            control: c,
            selectedTab,
          })
        );

    // Create Fuse instance for search
    const fuse = new Fuse(dataStateBasedOnSelected, {
      keys: searchAndFilteringList(agentType),
      threshold: 0.2,
      ignoreLocation: true,
      useExtendedSearch: true,
    });

    // Get filtered results
    const filteredResults = fuse
      .search(searchQuery)
      .map((result) => result.item)
      .filter(Boolean);

    return filteredResults.length;
  }, [searchQuery, agentType, controls, selectedTab, validControls.length]);

  const validControlNumber = filteredControlsCount;

  if (!agentType) {
    return null;
  }

  return (
    <div>
      {showNistCheckboxes && agentType === AgentSessionType.RISK_ASSESSMENT ? (
        <div className="flex items-center ">
          <ZButton
            isIconOnly
            size="sm"
            variant="light"
            className="w-6 h-6"
            onClick={() => {
              setShowNistCheckboxes(false);
              getCustomizeControlsActions().setSelectedControls(new Set<string>());
            }}
          >
            <X size={16} />
          </ZButton>
          <div className="text-sm font-medium">
          {`${selectedControls.size} control${selectedControls.size !== 1 ? 's':''} selected for bulk upload`}
          </div>
        </div>
      ) : showCheckboxes && (agentType === AgentSessionType.SOC2_GAP || 
          agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
          agentType === AgentSessionType.SOC2_GAP_TYPE2 ||
          agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) ? (
        <div className="flex items-center ">
          <ZButton
            isIconOnly
            size="sm"
            variant="light"
            className="w-6 h-6"
            onClick={() => {
              getCustomizeControlsActions().setShowCheckboxes(false);
              getCustomizeControlsActions().setSelectedControls(new Set<string>());
              getCustomizeControlsActions().setActiveBulkAction("")
            }}
          >
            <X size={16} />
          </ZButton>
          <div className="text-sm font-medium">
          {`${selectedControls.size} control${selectedControls.size !== 1 ? 's':''} selected for ${activeBulkAction === "upload" ? "bulk upload" : "deletion"}`}
          </div>
        </div>
      ) : (
        selectedControls.size > 0 ? (
        <>
          <div className="text-sm font-medium">
            {selectedControls.size} control
              {selectedControls.size > 1 ? "s" : ""} selected
          </div>
        </>
      ) : (
        <div className="flex ">
          <div className="text-sm font-medium">
            {!isCustomControls ? 'Upload Documents' : 'Finalize Controls'}
          </div>
          {agentType !== AgentSessionType.RISK_ASSESSMENT && (
            <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg ml-2">
              <p className="text-[10px] text-[#52525B] leading-4 font-medium">
                {validControlNumber}
              </p>
            </div>
          )}
        </div>
        )
      )}
    </div>
  );
};

export default CustomizeControlTopLeftSection;
